import type { FunctionAttributeNames } from '@dvb/common';

import {
  BASE_FONT_SCALE,
  DARK_MODE_CLASS_NAME,
  NO_TRANSITION_CLASS_NAME,
  safeUpdateFontScale,
} from '@dvb/common';
import { MotionGlobalConfig } from 'framer-motion';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

interface Setting {
  fontScale: number;
  hasAnimation: boolean;
  isDarkMode: boolean;
  reset: () => void;
  setFontScale: (scale: number) => void;
  toggleDarkMode: () => void;
  toggleHasAnimation: () => void;
}

const { classList } = document.documentElement;

// If you edit the storage name, please also update index.html
const STORAGE_NAME = 'setting-storage';

const initialState: Omit<Setting, FunctionAttributeNames<Setting>> = {
  fontScale: BASE_FONT_SCALE,
  hasAnimation: true,
  isDarkMode: false, // Always use light mode by default
};

export const useSettingStore = create<Setting>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        reset: () => {
          set(initialState);
        },
        setFontScale: (scale) => {
          const fontScale = safeUpdateFontScale(scale);
          set({ fontScale });
        },
        toggleDarkMode: () => {
          classList.toggle(DARK_MODE_CLASS_NAME);
          set((state) => ({ isDarkMode: !state.isDarkMode }));
        },
        toggleHasAnimation: () => {
          MotionGlobalConfig.skipAnimations = get().hasAnimation;
          classList.toggle(NO_TRANSITION_CLASS_NAME);
          set((state) => ({ hasAnimation: !state.hasAnimation }));
        },
      }),
      {
        name: STORAGE_NAME,
        partialize: (state) => {
          if (!state.hasAnimation) {
            classList.add(NO_TRANSITION_CLASS_NAME);
            MotionGlobalConfig.skipAnimations = true;
          }

          return {
            fontScale: state.fontScale,
            hasAnimation: state.hasAnimation,
            isDarkMode: state.isDarkMode,
          };
        },
        /**
         * Since we need to use the setting-storage in index.html to get the them style as soon as possible,
         * so we use localStorage (sync storage) instead of indexedDB (async storage) to store the setting data.
         */
        storage: createJSONStorage(() => localStorage),
      }
    ),
    { name: STORAGE_NAME }
  )
);
